import React, { useState, useEffect, useRef } from 'react';
import VirtualKeyboard from './VirtualKeyboard';
import metricsTracker from '../utils/metricsTracker';

const Addition = ({ updateMetrics }) => {
  const [num1, setNum1] = useState('');
  const [num2, setNum2] = useState('');
  const [userAnswer, setUserAnswer] = useState([]);
  const [carryOver, setCarryOver] = useState([]);
  const [carryPositions, setCarryPositions] = useState([]); // Track which positions need carry
  const [showSolution, setShowSolution] = useState(false);
  const [isCorrect, setIsCorrect] = useState(null);
  const [message, setMessage] = useState('');
  const [score, setScore] = useState(0);
  const [customNumbers, setCustomNumbers] = useState({ num1: '', num2: '' });
  const [useCustomNumbers, setUseCustomNumbers] = useState(false);
  const [gradeLevel, setGradeLevel] = useState('1');
  const [questionNumber, setQuestionNumber] = useState(1);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [solutionSteps, setSolutionSteps] = useState(null);
  
  // Virtual keyboard state
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  const [keyboardPosition, setKeyboardPosition] = useState({ x: 200, y: 100 }); // Initial position closer to input
  const [activeInput, setActiveInput] = useState(null);
  
  // Progress tracking state
  const [sessionStats, setSessionStats] = useState({
    totalAttempted: 0,
    totalCorrect: 0,
    totalShownSolutions: 0
  });
  
  // Button state management
  const [buttonsState, setButtonsState] = useState({
    checkAnswerDisabled: false,
    showSolutionDisabled: true
  });
  
  const mathProblemRef = useRef(null);

  // Level difficulty ranges - properly aligned with "Upto X digits"
  const gradeRanges = {
    '1': { min: 1, max: 99 },        // Upto 2 digits
    '2': { min: 10, max: 999 },      // Upto 3 digits
    '3': { min: 100, max: 9999 },    // Upto 4 digits
    '4': { min: 1000, max: 99999 },  // Upto 5 digits
    '5': { min: 10000, max: 999999 }, // Upto 6 digits
    '6': { min: 100000, max: 9999999 }, // Upto 7 digits
    '7': { min: 1000000, max: 99999999 }, // Upto 8 digits
    '8': { min: 10000000, max: 999999999 } // Upto 9 digits
  };

  // Calculate paper savings
  const calculatePaperSavings = () => {
    const linesPerQuestion = 3; // Each question takes about 3 lines (problem + working + answer)
    const validTotalQuestions = Number.isFinite(totalQuestions) ? totalQuestions : 0;
    const totalLines = validTotalQuestions * linesPerQuestion;
    const papersSaved = Math.ceil(totalLines / 25); // 25 lines per A4 sheet
    return { papersSaved, totalLines };
  };

  // Calculate the required length for answer (including carry-over possibility)
  const calculateAnswerLength = () => {
    const a = parseInt(num1) || 0;
    const b = parseInt(num2) || 0;
    const result = a + b;
    return result.toString().length;
  };

  // Generate random numbers for the problem
  const generateProblem = (isInitialLoad = false) => {
    let newNum1, newNum2;
    
    if (useCustomNumbers && customNumbers.num1 && customNumbers.num2) {
      newNum1 = customNumbers.num1;
      newNum2 = customNumbers.num2;
      setNum1(newNum1);
      setNum2(newNum2);
    } else {
      const range = gradeRanges[gradeLevel];
      newNum1 = Math.floor(Math.random() * (range.max - range.min + 1)) + range.min;
      newNum2 = Math.floor(Math.random() * (range.max - range.min + 1)) + range.min;
      
      setNum1(newNum1.toString());
      setNum2(newNum2.toString());
    }
    
    setUserAnswer([]);
    // Initialize carryOver array with proper length (result length - 1, since no carry box for 1s place)
    const resultLength = (parseInt(newNum1) + parseInt(newNum2)).toString().length;
    setCarryOver(new Array(resultLength - 1).fill(''));
    
    // Calculate which positions need carry
    const a = parseInt(newNum1);
    const b = parseInt(newNum2);
    const aStr = a.toString();
    const bStr = b.toString();
    let carry = 0;
    let positions = [];
    
    // Process from right to left, but carry boxes start from 10s place (index 0)
    for (let place = 0; place < resultLength; place++) {
      const digitA = parseInt(aStr[aStr.length - 1 - place] || 0);
      const digitB = parseInt(bStr[bStr.length - 1 - place] || 0);
      const sum = digitA + digitB + carry;
      const newCarry = Math.floor(sum / 10);
      
      // If there's a carry, add the carry box index
      // Carry boxes are indexed left to right: 0=leftmost, 1, 2, 3=rightmost
      // The carry from place N goes to place N+1, which has carry box index = (resultLength - 1) - (place + 1)
      if (newCarry > 0) {
        const carryBoxIndex = (resultLength - 1) - (place + 1); // Convert right-to-left place to left-to-right carry box index
        if (carryBoxIndex >= 0 && carryBoxIndex < resultLength - 1) { // Only add if it's a valid carry box index
          positions.push(carryBoxIndex);
        }
      }
      carry = newCarry;
    }
    setCarryPositions(positions);
    
    setShowSolution(false);
    setSolutionSteps(null);
    setIsCorrect(null);
    setMessage('');
    
    // Reset button states for new problem
    setButtonsState({
      checkAnswerDisabled: false,
      showSolutionDisabled: true
    });
    
    // Only increment question number if it's not the initial load
    if (!isInitialLoad) {
    setQuestionNumber(prev => prev + 1);
    }
    setTotalQuestions(prev => prev + 1);
    
    // Report metrics for new problem
    if (updateMetrics) {
      const { papersSaved } = calculatePaperSavings();
      updateMetrics(papersSaved);
    }
  };

  // Handle digit input changes
  const handleDigitChange = (index, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newAnswer = [...userAnswer];
      newAnswer[index] = value;
      setUserAnswer(newAnswer);
    }
  };

  // Handle carry over input
  const handleCarryChange = (index, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newCarry = [...carryOver];
      newCarry[index] = value;
      setCarryOver(newCarry);
    }
  };

  // Handle input focus - show keyboard and position it
                                                                                                                      const handleInputFocus = (index, type) => {
    setActiveInput({ index, type });
    setKeyboardVisible(true);
    
    // Position keyboard just next to the input
    const inputElement = document.querySelector(`input[data-index="${type}-${index}"][data-type="${type}"]`);
    if (inputElement) {
      const rect = inputElement.getBoundingClientRect();
      const mathProblemRect = mathProblemRef.current?.getBoundingClientRect();
      
      if (mathProblemRect) {
        // Calculate position based on place value - move left for higher place values
        const maxLength = Math.max(num1.length, num2.length);
        const answerLength = calculateAnswerLength();
        const placeValue = (type === 'answer' ? answerLength : maxLength) - index - 1; // 0 = ones, 1 = tens, 2 = hundreds, etc.
        
        // Base position: just next to input (5px offset)
        let x = rect.right - mathProblemRect.left + 5;
        
        // Move left for higher place values to avoid hiding questions
        if (placeValue >= 1) { // tens or higher
          x -= (placeValue * 15); // Move left by 15px per place value
        }
        
        // Ensure keyboard doesn't go too far left
        x = Math.max(x, 50);
        
        // Move keyboard down to avoid hiding the second number
        let y = rect.top - mathProblemRect.top;
        
        // If this is a carry field, move keyboard down more to avoid hiding first number
        if (type === 'carry') {
          y += 60; // Move down by 60px for carry fields
        } else {
          y += 40; // Move down by 40px for answer fields to avoid hiding second number
        }
        
        setKeyboardPosition({ x, y });
      }
    }
  };

  // Handle input blur - don't hide keyboard, don't clear active input for virtual keyboard
  const handleInputBlur = () => {
    // Don't hide keyboard, don't clear active input to allow virtual keyboard input
    // setActiveInput(null); // Commented out to allow virtual keyboard input
  };

  // Handle virtual keyboard input
  const handleVirtualKeyPress = (key) => {
    // Use activeInput state which is set when input is focused
    if (activeInput) {
      if (activeInput.type === 'answer') {
        handleDigitChange(activeInput.index, key);
      } else if (activeInput.type === 'carry') {
        handleCarryChange(activeInput.index, key);
      }
      
      // Re-focus the active input field after virtual keyboard input
      setTimeout(() => {
        const inputElement = document.querySelector(`input[data-index="${activeInput.type}-${activeInput.index}"]`);
        if (inputElement) {
          inputElement.focus();
        }
      }, 0);
    }
  };

  // Handle virtual keyboard backspace
  const handleVirtualBackspace = () => {
    // Use activeInput state which is set when input is focused
    if (activeInput) {
      if (activeInput.type === 'answer') {
        const newAnswer = [...userAnswer];
        newAnswer[activeInput.index] = '';
        setUserAnswer(newAnswer);
      } else if (activeInput.type === 'carry') {
        const newCarry = [...carryOver];
        newCarry[activeInput.index] = '';
        setCarryOver(newCarry);
      }
      
      // Re-focus the active input field after virtual keyboard backspace
      setTimeout(() => {
        const inputElement = document.querySelector(`input[data-index="${activeInput.type}-${activeInput.index}"]`);
        if (inputElement) {
          inputElement.focus();
        }
      }, 0);
    }
  };

  // Handle virtual keyboard clear
  const handleVirtualClear = () => {
    // Use activeInput state which is set when input is focused
    if (activeInput) {
      if (activeInput.type === 'answer') {
        const newAnswer = [...userAnswer];
        newAnswer[activeInput.index] = '';
        setUserAnswer(newAnswer);
      } else if (activeInput.type === 'carry') {
        const newCarry = [...carryOver];
        newCarry[activeInput.index] = '';
        setCarryOver(newCarry);
      }
      
      // Re-focus the active input field after virtual keyboard clear
      setTimeout(() => {
        const inputElement = document.querySelector(`input[data-index="${activeInput.type}-${activeInput.index}"]`);
        if (inputElement) {
          inputElement.focus();
        }
      }, 0);
    }
  };

  // Detailed step-by-step validation
  const validateAnswer = () => {
    const correctAnswer = parseInt(num1) + parseInt(num2);
    const userAnswerString = userAnswer.join('');
    const userAnswerInt = parseInt(userAnswerString) || 0;
    
    // Check if user has entered any answer
    if (userAnswerString.length === 0) {
      setMessage('Please enter your answer before checking.');
      setIsCorrect(false);
      return; // Don't proceed with validation or tracking
    }
    
    // Track attempt only if user has entered something
    setSessionStats(prev => ({
      ...prev,
      totalAttempted: prev.totalAttempted + 1
    }));
    
    // Always perform detailed validation to check carry-over and process
    const validationResult = performDetailedValidation();
    
    if (validationResult.isCorrect && validationResult.errors.length === 0) {
      setIsCorrect(true);
      setMessage('Correct! Well done! 🎉');
      setScore(prev => prev + 10);
      
      // Track correct answer
      setSessionStats(prev => ({
        ...prev,
        totalCorrect: prev.totalCorrect + 1
      }));
      
      // Track metrics for correct answer
      metricsTracker.trackCheckAnswer('addition', gradeLevel, true);
    } else {
      setIsCorrect(false);
      setMessage(validationResult.message);
      setScore(prev => Math.max(0, prev - 5));
      
      // Track metrics for incorrect answer
      metricsTracker.trackCheckAnswer('addition', gradeLevel, false);
    }
    
    // Disable Check Answer button after use
    setButtonsState(prev => ({
      ...prev,
      checkAnswerDisabled: true,
      showSolutionDisabled: false // Enable Show Solution after checking
    }));
  };

  // Perform detailed step-by-step validation
  const performDetailedValidation = () => {
    const a = parseInt(num1);
    const b = parseInt(num2);
    const expectedResult = a + b;
    const expectedResultString = expectedResult.toString();
    const userAnswerString = userAnswer.join('');
    
    // Check if user attempted all digits
    if (userAnswerString.length === 0) {
      return {
        isCorrect: false,
        message: 'Please attempt the problem. Enter your answer digit by digit.',
        errors: ['unattempted']
      };
    }
    
    // Check if user entered enough digits
    if (userAnswerString.length < expectedResultString.length) {
      return {
        isCorrect: false,
        message: `Incomplete answer. You need ${expectedResultString.length} digits. You entered ${userAnswerString.length}.`,
        errors: ['incomplete']
      };
    }
    
    // Check if user entered too many digits
    if (userAnswerString.length > expectedResultString.length) {
      return {
        isCorrect: false,
        message: `Too many digits. You need ${expectedResultString.length} digits. You entered ${userAnswerString.length}.`,
        errors: ['excess']
      };
    }
    
    // FIRST: Check if the answer is correct
    const answerCorrect = userAnswerString === expectedResult.toString();
    
    if (answerCorrect) {
      // Answer is correct, now check carry-over fields
      const carryValidation = validateCarryOverOnly();
      
      if (carryValidation.errors.length === 0) {
        return {
          isCorrect: true,
          message: 'Correct! Well done! 🎉',
          errors: []
        };
      } else {
        // Answer correct but missing carry-over
        return {
          isCorrect: true,
          message: `Correct answer! 🎉 However, you need to practice the complete process:\n\n${carryValidation.errors.map((error, index) => `${index + 1}. ${error.message}`).join('\n')}`,
          errors: carryValidation.errors
        };
      }
    } else {
      // Answer is wrong, find the first error starting from ones place
      const answerValidation = validateAnswerStepByStep();
      const errorMessage = generateDetailedErrorMessage(answerValidation);
      
      return {
        isCorrect: false,
        message: errorMessage,
        errors: answerValidation.errors
      };
    }
  };

  // Validate carry-over fields only (when answer is correct)
  const validateCarryOverOnly = () => {
    const a = parseInt(num1);
    const b = parseInt(num2);
    const expectedResult = a + b;
    
    let errors = [];
    let carry = 0;
    
    // Simple approach: process each place value from right to left
    const aStr = a.toString();
    const bStr = b.toString();
    const maxLength = Math.max(aStr.length, bStr.length);
    
    for (let place = 0; place < maxLength; place++) {
      // Get digits from right to left (place 0 = ones, place 1 = tens, etc.)
      const digitA = parseInt(aStr[aStr.length - 1 - place] || 0);
      const digitB = parseInt(bStr[bStr.length - 1 - place] || 0);
      
      const sum = digitA + digitB + carry;
      const newCarry = Math.floor(sum / 10);
      
      // Check if carry is needed for this place
      if (newCarry > 0) {
        const userCarry = parseInt(carryOver[place] || '');
        
        if (carryOver[place] === undefined || carryOver[place] === '') {
          errors.push({
            type: 'missing_carry',
            position: place,
            placeValue: getCarryPlaceValueName(place),
            expectedCarry: newCarry,
            message: `Missing carry-over in ${getCarryPlaceValueName(place)} place. You need to write ${newCarry} in the carry box.`
          });
        } else if (userCarry !== newCarry) {
          errors.push({
            type: 'wrong_carry',
            position: place,
            placeValue: getCarryPlaceValueName(place),
            expected: newCarry,
            actual: userCarry,
            message: `Wrong carry-over in ${getCarryPlaceValueName(place)} place. Expected ${newCarry}, got ${userCarry}.`
          });
        }
      } else {
        // Check for unnecessary carry
        const userCarry = parseInt(carryOver[place] || '');
        if (carryOver[place] !== undefined && carryOver[place] !== '' && !isNaN(userCarry) && userCarry > 0) {
          errors.push({
            type: 'unnecessary_carry',
            position: place,
            placeValue: getCarryPlaceValueName(place),
            actual: userCarry,
            message: `Unnecessary carry-over in ${getCarryPlaceValueName(place)} place. No carry needed here, but you wrote ${userCarry}.`
          });
        }
      }
      
      carry = newCarry;
    }
    
    return { errors };
  };

  // Validate answer step by step (when answer is wrong)
  const validateAnswerStepByStep = () => {
    const a = parseInt(num1);
    const b = parseInt(num2);
    const expectedResult = a + b;
    const expectedResultString = expectedResult.toString();
    
    let errors = [];
    let carry = 0;
    
    // SIMPLE APPROACH: Copy user answer and extract digits from right to left
    const userAnswerString = userAnswer.join('');
    
    // Process from right to left (ones place first) to find the first error
    for (let i = expectedResultString.length - 1; i >= 0; i--) {
      // Extract digits from right to left
      const positionFromRight = expectedResultString.length - 1 - i; // 0 = ones, 1 = tens, 2 = hundreds
      
      // Get the digit from user's answer (from right side)
      const userDigit = parseInt(userAnswerString.charAt(userAnswerString.length - 1 - positionFromRight) || '');
      
      // Get the digits from the numbers being added
      const digitA = parseInt(a.toString().padStart(expectedResultString.length, '0')[i] || 0);
      const digitB = parseInt(b.toString().padStart(expectedResultString.length, '0')[i] || 0);
      const expectedDigit = parseInt(expectedResultString[i]);
      
      // Check if user attempted this digit
      if (isNaN(userDigit)) {
        errors.push({
          type: 'unattempted',
          position: positionFromRight,
          placeValue: getPlaceValueName(positionFromRight),
          message: `You didn't attempt the ${getPlaceValueName(positionFromRight)} place`
        });
        break; // Stop at first error
      }
      
      // Calculate expected result for this step
      const stepSum = digitA + digitB + carry;
      const expectedStepResult = stepSum % 10;
      const newCarry = Math.floor(stepSum / 10);
      
      // Check if user's digit matches expected
      if (userDigit !== expectedStepResult) {
        errors.push({
          type: 'wrong_digit',
          position: positionFromRight,
          placeValue: getPlaceValueName(positionFromRight),
          expected: expectedStepResult,
          actual: userDigit,
          digitA,
          digitB,
          carry,
          message: `Wrong digit in ${getPlaceValueName(positionFromRight)} place. Expected ${expectedStepResult}, got ${userDigit}.`
        });
        break; // Stop at first error
      }
      
      carry = newCarry;
    }
    
    return { errors };
  };

  // Generate detailed error message with structured data for JSX rendering
  const generateDetailedErrorMessage = (validationSteps) => {
    if (validationSteps.errors.length === 0) {
      return { type: 'success', text: 'Correct! Well done! 🎉' };
    }
    
    return {
      type: 'error',
      errors: validationSteps.errors,
      correctAnswer: parseInt(num1) + parseInt(num2)
    };
  };

  // Get place value name
  const getPlaceValueName = (index) => {
    if (index === 0) return 'ones';
    if (index === 1) return 'tens';
    if (index === 2) return 'hundreds';
    if (index === 3) return 'thousands';
    if (index === 4) return 'ten thousands';
    if (index === 5) return 'hundred thousands';
    if (index === 6) return 'millions';
    return `${Math.pow(10, index)}s`;
  };

  // Get place value name for carry-over (carry-over affects the NEXT place value)
  const getCarryPlaceValueName = (index) => {
    if (index === 0) return 'tens';      // Carry from ones affects tens
    if (index === 1) return 'hundreds';  // Carry from tens affects hundreds
    if (index === 2) return 'thousands'; // Carry from hundreds affects thousands
    if (index === 3) return 'ten thousands';
    if (index === 4) return 'hundred thousands';
    if (index === 5) return 'millions';
    if (index === 6) return 'ten millions';
    return `${Math.pow(10, index + 1)}s`;
  };

  // Get CSS class for answer input based on validation state
  const getAnswerInputClassName = (index) => {
    if (isCorrect === null) return '';
    
    if (isCorrect) return 'correct';
    
    // Check if this specific digit has an error
    const validationResult = performDetailedValidation();
    if (validationResult.errors && validationResult.errors.length > 0) {
      // FIX: Map input index to validation position (left-to-right to right-to-left)
      // index 0 = leftmost (hundreds), index 1 = middle (tens), index 2 = rightmost (ones)
      // position 0 = ones (rightmost), position 1 = tens (middle), position 2 = hundreds (leftmost)
      const totalDigits = Math.max(num1.toString().length, num2.toString().length, (parseInt(num1) + parseInt(num2)).toString().length);
      const validationPosition = totalDigits - 1 - index; // Convert left-to-right index to right-to-left position
      
      const digitError = validationResult.errors.find(error => error.position === validationPosition);
      if (digitError) {
        if (digitError.type === 'unattempted') return 'unattempted';
        if (digitError.type === 'wrong_digit') return 'incorrect';
        if (digitError.type === 'invalid_digit') return 'invalid';
      }
    }
    
    return '';
  };

  // Get background color for answer input based on validation state
  const getAnswerInputBackground = (index) => {
    if (isCorrect === null) return 'transparent';
    
    if (isCorrect) return '#d4edda'; // Light green for correct
    
    // Check if this specific digit has an error
    const validationResult = performDetailedValidation();
    if (validationResult.errors && validationResult.errors.length > 0) {
      // FIX: Map input index to validation position (left-to-right to right-to-left)
      const totalDigits = Math.max(num1.toString().length, num2.toString().length, (parseInt(num1) + parseInt(num2)).toString().length);
      const validationPosition = totalDigits - 1 - index; // Convert left-to-right index to right-to-left position
      
      const digitError = validationResult.errors.find(error => error.position === validationPosition);
      if (digitError) {
        if (digitError.type === 'unattempted') return '#fff3cd'; // Light yellow for unattempted
        if (digitError.type === 'wrong_digit') return '#f8d7da'; // Light red for wrong digit
        if (digitError.type === 'invalid_digit') return '#f8d7da'; // Light red for invalid digit
      }
    }
    
    return 'transparent';
  };

  // Get CSS class for carry-over input based on validation state
  const getCarryInputClassName = (index) => {
    if (isCorrect === null) return '';
    
    // Only show highlighting after validation has been performed
    if (isCorrect === false || (isCorrect === true && message.includes('However'))) {
      // Check if this position needs carry and user didn't enter it
      if (carryPositions.includes(index)) {
        const userCarry = parseInt(carryOver[index] || '');
        if (carryOver[index] === undefined || carryOver[index] === '') {
          return 'missing_carry';
        } else if (userCarry !== 1) { // For now, assume all carries are 1
          return 'wrong_carry';
        }
      } else {
        // Check for unnecessary carry
        const userCarry = parseInt(carryOver[index] || '');
        if (carryOver[index] !== undefined && carryOver[index] !== '' && !isNaN(userCarry) && userCarry > 0) {
          return 'unnecessary_carry';
        }
      }
    }
    
    return '';
  };

  // Get background color for carry-over input based on validation state
  const getCarryInputBackground = (index) => {
    if (isCorrect === null) return '#fff3e0'; // Default light orange
    
    // Only show highlighting after validation has been performed
    if (isCorrect === false || (isCorrect === true && message.includes('However'))) {
      // Check if this position needs carry and user didn't enter it
      if (carryPositions.includes(index)) {
        const userCarry = parseInt(carryOver[index] || '');
        if (carryOver[index] === undefined || carryOver[index] === '') {
          return '#fff3cd'; // Light yellow for missing
        } else if (userCarry !== 1) { // For now, assume all carries are 1
          return '#f8d7da'; // Light red for wrong
        }
      } else {
        // Check for unnecessary carry
        const userCarry = parseInt(carryOver[index] || '');
        if (carryOver[index] !== undefined && carryOver[index] !== '' && !isNaN(userCarry) && userCarry > 0) {
          return '#fff3cd'; // Light yellow for unnecessary
        }
      }
    }
    
    return '#fff3e0'; // Default light orange
  };

  // Render error message with proper styling
  const renderErrorMessage = (messageData) => {
    if (messageData.type === 'success') {
      return <div>{messageData.text}</div>;
    }
    
    if (messageData.type === 'error') {
      return (
        <div>
          <div style={{ fontWeight: 'bold', marginBottom: '10px', color: '#dc3545' }}>
            Incorrect answer. Here are the issues:
          </div>
          
          {messageData.errors.map((error, index) => (
            <div key={index} style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #dee2e6' }}>
              <div style={{ fontWeight: 'bold', color: '#dc3545', marginBottom: '5px' }}>
                {index + 1}. {error.message}
              </div>
              
              {error.type === 'wrong_digit' && (
                <div style={{ marginLeft: '20px', color: '#6c757d' }}>
                  <div>Calculation: <span style={{ color: '#dc3545', fontWeight: 'bold' }}>{error.digitA} + {error.digitB}</span>
                    {error.carry > 0 && <span style={{ color: '#dc3545', fontWeight: 'bold' }}> + {error.carry} (carry)</span>}
                    <span style={{ color: '#dc3545', fontWeight: 'bold' }}> = {error.digitA + error.digitB + error.carry}</span>
                  </div>
                  <div style={{ color: '#dc3545', fontWeight: 'bold' }}>
                    You should write {error.expected} in the {error.placeValue} place
                  </div>
                </div>
              )}
              
              {error.type === 'missing_carry' && (
                <div style={{ marginLeft: '20px', color: '#6c757d' }}>
                  This affects the next place value calculation
                </div>
              )}
              
              {error.type === 'wrong_carry' && (
                <div style={{ marginLeft: '20px', color: '#6c757d' }}>
                  This will cause errors in subsequent calculations
                </div>
              )}
              
              {error.type === 'unnecessary_carry' && (
                <div style={{ marginLeft: '20px', color: '#6c757d' }}>
                  Remove this carry-over as it's not needed
                </div>
              )}
            </div>
          ))}
          
          <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#d4edda', color: '#155724', borderRadius: '6px', fontWeight: 'bold', textAlign: 'center' }}>
            The correct answer is {messageData.correctAnswer}.
          </div>
        </div>
      );
    }
    
    // Fallback for plain text messages
    return <div style={{ whiteSpace: 'pre-line' }}>{messageData}</div>;
  };

  // Show solution with step-by-step explanation
  const showSolutionSteps = () => {
    const { steps, result } = getSolutionSteps();
    setSolutionSteps({ steps, result });
    setShowSolution(true);
    
    // Track solution view
    setSessionStats(prev => ({
      ...prev,
      totalShownSolutions: prev.totalShownSolutions + 1
    }));
    
    // Track metrics for show solution
    metricsTracker.trackShowSolution('addition', gradeLevel);
    
    // Disable Show Solution button after use
    setButtonsState(prev => ({
      ...prev,
      showSolutionDisabled: true
    }));
  };

  // Calculate the correct answer and steps with place values
  const getSolutionSteps = () => {
    const a = parseInt(num1);
    const b = parseInt(num2);
    const result = a + b;
    
    const aDigits = a.toString().split('').reverse();
    const bDigits = b.toString().split('').reverse();
    const maxLength = Math.max(aDigits.length, bDigits.length);
    
    let steps = [];
    let carry = 0;
    
    for (let i = 0; i < maxLength; i++) {
      const digitA = parseInt(aDigits[i] || 0);
      const digitB = parseInt(bDigits[i] || 0);
      const sum = digitA + digitB + carry;
      const resultDigit = sum % 10;
      const newCarry = Math.floor(sum / 10);
      
      let placeValue = '';
      if (i === 0) placeValue = 'ones';
      else if (i === 1) placeValue = 'tens';
      else if (i === 2) placeValue = 'hundreds';
      else if (i === 3) placeValue = 'thousands';
      else if (i === 4) placeValue = 'ten thousands';
      else if (i === 5) placeValue = 'hundred thousands';
      else if (i === 6) placeValue = 'millions';
      else placeValue = `${Math.pow(10, i)}s`;
      
      steps.push({
        position: i + 1,
        digitA,
        digitB,
        carry: carry,
        sum,
        resultDigit,
        newCarry: newCarry,
        placeValue
      });
      
      carry = newCarry;
    }
    
    if (carry > 0) {
      steps.push({
        position: maxLength + 1,
        digitA: 0,
        digitB: 0,
        carry: carry,
        sum: carry,
        resultDigit: carry,
        newCarry: 0,
        placeValue: maxLength === 1 ? 'tens' : maxLength === 2 ? 'hundreds' : maxLength === 3 ? 'thousands' : `${Math.pow(10, maxLength)}s`
      });
    }
    
    return { steps, result };
  };

  // Improved keyboard navigation
  const handleKeyDown = (e, currentIndex, type) => {
    if (e.key === 'Enter') {
      validateAnswer();
    } else if (e.key === 'Tab') {
      e.preventDefault();
      const inputs = document.querySelectorAll('input[data-index]');
      const currentInputIndex = Array.from(inputs).findIndex(input => input === e.target);
      
      if (e.shiftKey) {
        // Shift+Tab - move to previous input
        if (currentInputIndex > 0) {
          inputs[currentInputIndex - 1].focus();
        }
      } else {
        // Tab - move to next input
        if (currentInputIndex < inputs.length - 1) {
          inputs[currentInputIndex + 1].focus();
        }
      }
    }
    // Removed automatic cursor movement on backspace and digit entry
    // Let students decide where to place cursor
  };

  // Hide keyboard when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't clear activeInput when clicking on virtual keyboard or input fields
      if (!event.target.closest('.virtual-keyboard') && 
          !event.target.closest('.digit-input') && 
          !event.target.closest('.carry-input')) {
        setKeyboardVisible(false);
        setActiveInput(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Generate initial problem on component mount
  useEffect(() => {
    generateProblem(true); // Pass true for initial load
  }, [gradeLevel]);

  const expectedLength = solutionSteps ? solutionSteps.result.toString().length : 0;
  
  // Calculate alignment for proper place value positioning
  const maxLength = Math.max(num1.length, num2.length, expectedLength);
  
  // Pad numbers with spaces for proper alignment
  const padNumber = (num, length) => {
    return num.padStart(length, ' ');
  };

  const { papersSaved, totalLines } = calculatePaperSavings();

  return (
    <div>
      {/* Compact Header */}
      <div style={{ marginBottom: '15px' }}>
        <h2 style={{ margin: '0', fontSize: '1.6rem', color: '#333' }}>Addition Practice</h2>
      </div>

      {/* Compact Controls Row */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '15px', 
        marginBottom: '15px',
        flexWrap: 'wrap'
      }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ fontSize: '0.9rem', fontWeight: '500' }}>Level:</label>
          <select 
            value={gradeLevel} 
            onChange={(e) => {
              setGradeLevel(e.target.value);
              // Track level selection when user clicks dropdown
              metricsTracker.trackLevelSelected('addition', e.target.value);
            }}
            style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #ddd' }}
          >
            {Array.from({ length: 8 }, (_, i) => i + 1).map(level => (
              <option 
                key={level} 
                value={level.toString()}
                disabled={level > 3}
                style={{ color: level > 3 ? '#999' : 'inherit' }}
              >
                {level > 3 ? `Level ${level} (Upto ${level + 1} digits) - Upgrade to Pro Edition` : `Level ${level} (Upto ${level + 1} digits)`}
              </option>
            ))}
          </select>
        </div>
        {parseInt(gradeLevel) > 3 && (
          <div style={{ 
            marginTop: '8px', 
            padding: '8px 12px', 
            backgroundColor: '#fff3cd', 
            border: '1px solid #ffeaa7', 
            borderRadius: '4px',
            fontSize: '14px',
            color: '#856404'
          }}>
            ⚠️ Upgrade to Pro Edition to support Level {gradeLevel} (Upto {parseInt(gradeLevel) + 1} digits)
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ fontSize: '0.9rem', fontWeight: '500' }}>
            <input 
              type="checkbox" 
              checked={useCustomNumbers}
              onChange={(e) => setUseCustomNumbers(e.target.checked)}
              style={{ marginRight: '5px' }}
            />
            Custom Numbers
          </label>
        </div>

          {useCustomNumbers && (
          <div style={{ display: 'flex', gap: '8px', flexDirection: 'column' }}>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="number"
                placeholder="1st"
                value={customNumbers.num1}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.length <= 4) {
                    setCustomNumbers(prev => ({ ...prev, num1: value }));
                  }
                }}
                style={{ width: '100px', padding: '4px 8px', borderRadius: '4px', border: '1px solid #ddd' }}
              />
              <input
                type="number"
                placeholder="2nd"
                value={customNumbers.num2}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.length <= 4) {
                    setCustomNumbers(prev => ({ ...prev, num2: value }));
                  }
                }}
                style={{ width: '100px', padding: '4px 8px', borderRadius: '4px', border: '1px solid #ddd' }}
              />
            </div>
            {(customNumbers.num1.length > 4 || customNumbers.num2.length > 4) && (
              <div style={{ 
                fontSize: '0.8rem', 
                color: '#e74c3c', 
                marginTop: '4px',
                padding: '4px 8px',
                backgroundColor: '#fdf2f2',
                borderRadius: '4px',
                border: '1px solid #fecaca'
              }}>
                ⚠️ Upgrade to Pro Edition to use numbers with more than 4 digits
            </div>
          )}
        </div>
        )}

        <button className="btn btn-primary" onClick={generateProblem} style={{ padding: '6px 12px' }}>
          Generate Problem
        </button>
      </div>

      {/* Compact Progress Display */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '25px',
        marginBottom: '15px',
        padding: '8px 15px',
        backgroundColor: '#f8f9fa',
        borderRadius: '6px',
        border: '1px solid #dee2e6',
        fontSize: '0.9rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <span style={{ color: '#6c757d' }}>Attempted:</span>
          <span style={{ fontWeight: 'bold', color: '#667eea' }}>{sessionStats.totalAttempted}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <span style={{ color: '#6c757d' }}>Correct:</span>
          <span style={{ fontWeight: 'bold', color: '#28a745' }}>{sessionStats.totalCorrect}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <span style={{ color: '#6c757d' }}>Solutions:</span>
          <span style={{ fontWeight: 'bold', color: '#ffc107' }}>{sessionStats.totalShownSolutions}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <span style={{ color: '#6c757d' }}>Success:</span>
          <span style={{ fontWeight: 'bold', color: sessionStats.totalAttempted > 0 ? '#28a745' : '#6c757d' }}>
            {sessionStats.totalAttempted > 0 ? Math.round((sessionStats.totalCorrect / sessionStats.totalAttempted) * 100) : 0}%
          </span>
        </div>
      </div>

      {/* Question Display */}
      <div className="question-number">
        {num1}+{num2}=?
      </div>

      {/* Practice Layout - Problem and Solution Side by Side */}
      <div className="practice-layout">
        {/* Problem Section */}
        <div className="problem-section">
          {/* Math Problem */}
          <div className="math-problem" ref={mathProblemRef} style={{ position: 'relative' }}>
            {/* Grid container for perfect alignment */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: `40px repeat(${Math.max(maxLength, calculateAnswerLength())}, 40px) auto`,
              gap: '0px',
              alignItems: 'center'
            }}>
              {/* Carry-over row */}
              <div></div> {/* Empty cell for operator space */}
              {Array.from({ length: Math.max(maxLength, calculateAnswerLength()) }, (_, index) => (
                <div key={`carry-${index}`} style={{ textAlign: 'center' }}>
                  {index < Math.max(maxLength, calculateAnswerLength()) - 1 && (
                    <input
                      type="text"
                    className={`carry-input ${getCarryInputClassName(index)}`}
                      value={carryOver[index] || ''}
                    title={`Carry box ${index} (${getCarryPlaceValueName(index)} place)`}
                      onChange={(e) => handleCarryChange(index, e.target.value)}
                      onFocus={(e) => handleInputFocus(index, 'carry')}
                      onBlur={handleInputBlur}
                      onKeyDown={(e) => handleKeyDown(e, index, 'carry')}
                      data-index={`carry-${index}`}
                      data-type="carry"
                      placeholder=""
                      style={{
                        width: '30px',
                        height: '25px',
                        fontSize: '0.8rem',
                        textAlign: 'center',
                        border: 'none',
                        borderBottom: '2px solid #e67e22',
                        borderRadius: '0',
                      backgroundColor: getCarryInputBackground(index),
                        color: '#e67e22',
                        fontWeight: 'bold'
                      }}
                    />
                  )}
                </div>
              ))}
              <div style={{ fontSize: '0.8rem', color: '#e67e22', fontWeight: 'bold' }}>
                Carry
              </div>

              {/* First number row */}
              <div></div> {/* Empty cell for operator space */}
              {padNumber(num1, Math.max(maxLength, calculateAnswerLength())).split('').map((digit, index) => (
                <input
                  key={`num1-${index}`}
                  type="text"
                  className="digit-input"
                  value={digit === ' ' ? '' : digit}
                  readOnly
                  style={{ 
                    backgroundColor: '#e8f4fd',
                    width: '40px',
                    height: '50px',
                    textAlign: 'center',
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    border: '2px solid #ddd',
                    borderRadius: '4px'
                  }}
                />
              ))}
              <div></div> {/* Empty cell for carry label */}

              {/* Second number row with operator */}
              <span className="operator" style={{ textAlign: 'center' }}>+</span>
              {padNumber(num2, Math.max(maxLength, calculateAnswerLength())).split('').map((digit, index) => (
                <input
                  key={`num2-${index}`}
                  type="text"
                  className="digit-input"
                  value={digit === ' ' ? '' : digit}
                  readOnly
                  style={{ 
                    backgroundColor: '#e8f4fd',
                    width: '40px',
                    height: '50px',
                    textAlign: 'center',
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    border: '2px solid #ddd',
                    borderRadius: '4px'
                  }}
                />
              ))}
              <div></div> {/* Empty cell for carry label */}

              {/* Line row */}
              <div></div> {/* Empty cell for operator space */}
              {Array.from({ length: Math.max(maxLength, calculateAnswerLength()) }, (_, index) => (
                <div key={`line-${index}`} style={{ 
                  height: '2px', 
                  backgroundColor: '#333',
                  margin: '10px 0'
                }}></div>
              ))}
              <div></div> {/* Empty cell for carry label */}

              {/* Answer row */}
              <div></div> {/* Empty cell for operator space */}
              {Array.from({ length: calculateAnswerLength() }, (_, index) => (
                <div key={`answer-container-${index}`} style={{ 
                  width: '40px', 
                  height: '50px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative'
                }}>
                  <input
                    key={`answer-${index}`}
                    type="text"
                    className={`digit-input ${getAnswerInputClassName(index)}`}
                    value={userAnswer[index] || ''}
                    onChange={(e) => handleDigitChange(index, e.target.value)}
                    onFocus={(e) => handleInputFocus(index, 'answer')}
                    onBlur={handleInputBlur}
                    onKeyDown={(e) => handleKeyDown(e, index, 'answer')}
                    data-index={`answer-${index}`}
                    data-type="answer"
                    maxLength={1}
                    placeholder=""
                    style={{
                      width: '20px',
                      height: '30px',
                      textAlign: 'center',
                      fontSize: '1.5rem',
                      fontWeight: 'bold',
                      border: 'none',
                      borderRadius: '0',
                      backgroundColor: getAnswerInputBackground(index),
                      transition: 'border-color 0.3s ease',
                      outline: 'none'
                    }}
                  />
                  {/* Dash underline */}
                  <div style={{
                    position: 'absolute',
                    bottom: '5px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '25px',
                    height: '3px',
                    backgroundColor: '#333',
                    borderRadius: '1px'
                  }}></div>
                </div>
              ))}
              <div></div> {/* Empty cell for carry label */}
            </div>

            {/* Virtual Keyboard */}
            <VirtualKeyboard
              onKeyPress={handleVirtualKeyPress}
              onBackspace={handleVirtualBackspace}
              onClear={handleVirtualClear}
              position={keyboardPosition}
              isVisible={keyboardVisible}
            />
          </div>

          {/* Result Message */}
          {message && (
            <div className={`result ${isCorrect ? 'success' : 'error'}`}>
              {isCorrect ? (
                <div>{message}</div>
              ) : (
                <div style={{ textAlign: 'left' }}>
                  {renderErrorMessage(message)}
                </div>
              )}
            </div>
          )}

          {/* Controls */}
          <div className="controls">
            <button 
              className="btn btn-success" 
              onClick={validateAnswer}
              disabled={buttonsState.checkAnswerDisabled}
              style={{
                opacity: buttonsState.checkAnswerDisabled ? 0.6 : 1,
                cursor: buttonsState.checkAnswerDisabled ? 'not-allowed' : 'pointer'
              }}
            >
              {buttonsState.checkAnswerDisabled ? 'Already Checked' : 'Check Answer'}
            </button>
            <button 
              className="btn btn-warning" 
              onClick={showSolutionSteps}
              disabled={buttonsState.showSolutionDisabled}
              style={{
                opacity: buttonsState.showSolutionDisabled ? 0.6 : 1,
                cursor: buttonsState.showSolutionDisabled ? 'not-allowed' : 'pointer'
              }}
            >
              Show Solution
            </button>
          </div>
        </div>

        {/* Solution Section */}
        {showSolution && solutionSteps && (
          <div className="solution-section">
            <h3>Step-by-Step Solution</h3>
            <div className="solution-steps">
              {solutionSteps.steps.map((step, index) => (
                <div key={index} style={{ marginBottom: '15px', padding: '10px', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #e9ecef' }}>
                  <strong style={{ color: '#667eea' }}>Step {step.position} ({step.placeValue} place):</strong>
                  <div style={{ marginTop: '5px', fontFamily: 'monospace' }}>
                    <span style={{ color: '#333', backgroundColor: '#e8f4fd', padding: '2px 4px', borderRadius: '3px' }}>{step.digitA}</span> + 
                    <span style={{ color: '#333', backgroundColor: '#e8f4fd', padding: '2px 4px', borderRadius: '3px' }}>{step.digitB}</span>
                    {step.carry > 0 && ` + `}
                    {step.carry > 0 && <span style={{ color: '#e67e22', backgroundColor: '#fff3e0', padding: '2px 4px', borderRadius: '3px' }}>{step.carry} (carry)</span>} = {step.sum}
                    {step.sum >= 10 && ` → `}
                    {step.sum >= 10 && <span style={{ color: '#155724', backgroundColor: '#d4edda', padding: '2px 4px', borderRadius: '3px' }}>{step.resultDigit}</span>}
                    {step.sum >= 10 && ` (write ${step.resultDigit}, carry ${step.newCarry})`}
                    {step.sum < 10 && ` → `}
                    {step.sum < 10 && <span style={{ color: '#155724', backgroundColor: '#d4edda', padding: '2px 4px', borderRadius: '3px' }}>{step.resultDigit}</span>}
                  </div>
                </div>
              ))}
              <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#667eea', color: 'white', borderRadius: '6px', fontWeight: 'bold', textAlign: 'center' }}>
                Final Answer: {solutionSteps.result}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Addition; 